import { format as originalFormat } from 'date-fns';
import * as React from 'react';
import { memo, useCallback, useMemo } from 'react';

import { FlightTurnaroundDto, useQueryGetGetFlights } from '../../../../../backend';
import { useRmsGanttValue } from '../../RmsContext';
import { ganttDayFormat } from '../gantt.constants';
import { generateGanttTurnaroundRows, getNbTurnaroundsByGroup, groupTurnaroundsByResourceType } from '../gantt.utils';
import { CollapsibleGanttGroup } from './CollapsibleGanttGroup';
import { GanttGroupName } from './GanttGroup.style';

export const GanttResourceGroups = memo(function GanttResourceGroups({
    children,
}: {
    children?: (turnarounds: FlightTurnaroundDto[][], resourceName?: string) => React.ReactNode;
}) {
    const day = useRmsGanttValue('day');
    const resourceType = useRmsGanttValue('resourceType');

    const { data: ganttDto } = useQueryGetGetFlights({
        selectedDay: originalFormat(day, ganttDayFormat),
        selectedResourceType: resourceType,
    });

    const turnarounds = ganttDto?.turnarounds;
    const resourcesByType = useRmsGanttValue('resourcesByType');
    const proposedAllocation = useRmsGanttValue('proposedAllocation');

    const turnaroundsByResources = useMemo(() => {
        return groupTurnaroundsByResourceType(turnarounds, resourceType, proposedAllocation, resourcesByType);
    }, [turnarounds, resourceType, proposedAllocation, resourcesByType]);
    const orderedKeys: string[] = useMemo(() => {
        let keys = [];
        if (resourcesByType) {
            keys = resourcesByType.map(r => r.name);
        }
        keys.unshift('');
        return keys;
    }, [resourcesByType]);

    const getTurnaroundsInResourceGroup = useCallback(
        (resourceName: string) => {
            const minTimeBetweenUse = resourcesByType.find(a => a.name === resourceName)?.minTimeBetweenUse;
            return generateGanttTurnaroundRows(turnaroundsByResources[resourceName], minTimeBetweenUse || 0);
        },
        [resourcesByType, turnaroundsByResources],
    );

    return turnarounds?.length > 0 ? (
        <>
            {orderedKeys.map((resourceName, index) => {
                const turnaroundsInResourceGroup: FlightTurnaroundDto[][] = getTurnaroundsInResourceGroup(resourceName);
                return (
                    <CollapsibleGanttGroup
                        index={index}
                        firstOrLast={index === 0 || index === orderedKeys.length - 1}
                        key={resourceName || '-'}
                        groupNamelabel={<GanttGroupName>{resourceName || '-'}</GanttGroupName>}
                        nbRows={turnaroundsInResourceGroup?.length}
                        nbTurnarounds={getNbTurnaroundsByGroup(turnaroundsInResourceGroup)}
                        resourceName={resourceName}
                    >
                        {children?.(turnaroundsInResourceGroup, resourceName)}
                    </CollapsibleGanttGroup>
                );
            })}
        </>
    ) : null;
});
